/**
 * Tracked Firestore — Re-exports all firebase/firestore.
 * Wraps 6 CRUD functions with telemetry tracking.
 * Import from '@/lib/firestore' instead of 'firebase/firestore'.
 */

// Re-export EVERYTHING from firebase/firestore
export * from 'firebase/firestore'

// Import originals under different names
import {
  getDoc as originalGetDoc,
  getDocs as originalGetDocs,
  addDoc as originalAddDoc,
  updateDoc as originalUpdateDoc,
  deleteDoc as originalDeleteDoc,
  setDoc as originalSetDoc,
} from 'firebase/firestore'

import { trackFirestoreOp } from '@/services/telemetry'

function collName(ref: any): string {
  try {
    if (ref?.path) return ref.path.split('/')[0] || 'unknown'
    if (ref?.firestore && ref?._query?.path?.segments) return ref._query.path.segments[0] || 'unknown'
    if (ref?.type === 'query' || ref?.type === 'collection') {
      const q = ref as any
      return q._query?.path?.segments?.[0] || q.path?.split('/')[0] || 'unknown'
    }
  } catch { /* ignore */ }
  return 'unknown'
}

// Override with tracked versions (same signatures)
export const getDoc: typeof originalGetDoc = async (ref) => {
  const s = Date.now()
  const r = await originalGetDoc(ref)
  trackFirestoreOp({ collection: collName(ref), operation: 'get', documentId: ref.id, reads: 1, latencyMs: Date.now() - s })
  return r
}

export const getDocs: typeof originalGetDocs = async (q) => {
  const s = Date.now()
  const r = await originalGetDocs(q)
  trackFirestoreOp({ collection: collName(q as any), operation: 'query', reads: r.size, latencyMs: Date.now() - s })
  return r
}

export const addDoc: typeof originalAddDoc = async (ref, data) => {
  const s = Date.now()
  const r = await originalAddDoc(ref, data)
  trackFirestoreOp({ collection: collName(ref), operation: 'create', documentId: r.id, writes: 1, latencyMs: Date.now() - s })
  return r
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const updateDoc = (async (ref: any, ...args: any[]) => {
  const s = Date.now()
  const r = await (originalUpdateDoc as any)(ref, ...args)
  trackFirestoreOp({ collection: collName(ref), operation: 'update', documentId: ref.id, writes: 1, latencyMs: Date.now() - s })
  return r
}) as typeof originalUpdateDoc

export const deleteDoc: typeof originalDeleteDoc = async (ref) => {
  const s = Date.now()
  const r = await originalDeleteDoc(ref)
  trackFirestoreOp({ collection: collName(ref), operation: 'delete', documentId: ref.id, deletes: 1, latencyMs: Date.now() - s })
  return r
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const setDoc = (async (ref: any, ...args: any[]) => {
  const s = Date.now()
  const r = await (originalSetDoc as any)(ref, ...args)
  trackFirestoreOp({ collection: collName(ref), operation: 'set', documentId: ref.id, writes: 1, latencyMs: Date.now() - s })
  return r
}) as typeof originalSetDoc
